import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Bike, Phone, MapPin, CheckCircle2, AlertTriangle, LogOut, RefreshCw, Euro, Clock } from 'lucide-react';

import { getBackendUrl } from '../services/origin';
import { registerRiderPush, isPushSupported } from '../services/push';
const API = getBackendUrl();

function api() {
  const token = localStorage.getItem('rider_token');
  return axios.create({
    baseURL: `${API}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

const TABS = [
  { key: 'active', label: 'In corso', color: '#D44E20' },
  { key: 'delivered', label: 'Consegnate', color: '#10B981' },
  { key: 'issues', label: 'Problemi', color: '#F59E0B' },
];

export default function RiderDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState('active');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [issueModal, setIssueModal] = useState(null);
  const [issueReason, setIssueReason] = useState('');

  const logout = () => { localStorage.removeItem('rider_token'); localStorage.removeItem('rider_name'); nav('/rider/login'); };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [mR, oR] = await Promise.all([
        me ? Promise.resolve({ data: me }) : api().get('/rider/me'),
        api().get(`/rider/orders?status=${tab}`),
      ]);
      if (!me) setMe(mR.data);
      setOrders(oR.data || []);
    } catch (e) {
      if (e.response?.status === 401) { logout(); return; }
      toast.error('Errore caricamento');
    } finally { setLoading(false); }
  }, [tab, me]);

  useEffect(() => {
    if (!localStorage.getItem('rider_token')) { nav('/rider/login'); return; }
    load();
    // Best-effort: subscribe this rider to push so they get new-delivery alerts.
    if (isPushSupported()) {
      const t = localStorage.getItem('rider_token');
      if (t) registerRiderPush(t);
    }
    const iv = setInterval(() => load(true), 15000);
    return () => clearInterval(iv);
  }, [load, nav]);

  const markDelivered = async (o) => {
    const msg = o.payment_method === 'cod' ? `Consegnata? Hai incassato ${o.total.toFixed(2)}€ in contanti?` : 'Consegnata?';
    if (!window.confirm(msg)) return;
    try {
      await api().post(`/rider/orders/${o.id}/delivered`);
      toast.success('Consegnata ✅');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const reportIssue = async () => {
    if (!issueModal) return;
    try {
      await api().post(`/rider/orders/${issueModal.id}/issue`, { reason: issueReason });
      toast.success('Segnalazione inviata al locale');
      setIssueModal(null); setIssueReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const mapsLink = (q) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="rider-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#D44E20] to-[#F59E0B] text-white px-5 py-5 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Bike size={20}/></div>
          <div>
            <p className="text-xs opacity-90">Ciao</p>
            <p className="text-base font-bold">{me?.name || localStorage.getItem('rider_name') || 'Rider'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} data-testid="refresh-btn" className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><RefreshCw size={15}/></button>
          <button onClick={logout} data-testid="logout-btn" className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><LogOut size={15}/></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-3 py-3 flex gap-2 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className="px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all"
            style={{
              backgroundColor: tab === t.key ? t.color : '#fff',
              color: tab === t.key ? '#fff' : '#64748B',
              border: `1px solid ${tab === t.key ? t.color : '#e2e8f0'}`,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Orders */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="px-6 py-20 text-center">
          <Bike size={48} className="mx-auto text-slate-300 mb-3"/>
          <p className="text-sm text-[#64748B]">
            {tab === 'active' ? 'Nessuna consegna attiva. Rimani pronto! 🏍️' :
             tab === 'delivered' ? 'Nessuna consegna completata negli ultimi 7 giorni' :
             'Nessun problema segnalato'}
          </p>
        </div>
      ) : (
        <div className="px-3 space-y-3">
          {orders.map(o => (
            <div key={o.id} data-testid={`rider-order-${o.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Order header */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#64748B]">Ordine</p>
                  <p className="text-base font-bold text-[#1A1A1A]">#{o.order_number}</p>
                </div>
                <div className="text-right">
                  {o.payment_method === 'cod' && o.cash_to_collect > 0 ? (
                    <>
                      <p className="text-[10px] font-bold text-amber-600">💶 DA INCASSARE</p>
                      <p className="text-lg font-bold text-amber-700">{o.cash_to_collect.toFixed(2)}€</p>
                    </>
                  ) : (
                    <>
                      <p className="text-[10px] text-[#64748B]">Già pagato</p>
                      <p className="text-base font-bold text-emerald-600">{o.total.toFixed(2)}€</p>
                    </>
                  )}
                </div>
              </div>

              {/* Address block */}
              <div className="px-4 py-3 space-y-2">
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 text-[#D44E20] flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A]">{o.customer_name || 'Cliente'}</p>
                    <p className="text-xs text-[#64748B]">{o.street}</p>
                    <p className="text-xs text-[#64748B]">{o.zip} {o.city}</p>
                    {o.intercom && <p className="text-xs text-[#64748B]">🔔 Citofono: <b>{o.intercom}</b></p>}
                    {o.notes_delivery && <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1">📝 {o.notes_delivery}</p>}
                  </div>
                </div>

                {/* Quick action buttons */}
                <div className="flex gap-2 pt-1">
                  <a href={mapsLink(o.maps_query)} target="_blank" rel="noopener noreferrer" data-testid={`maps-btn-${o.id}`}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-bold border border-blue-100 hover:bg-blue-100">
                    🗺️ Naviga
                  </a>
                  {o.phone && (
                    <a href={`tel:${o.phone}`} data-testid={`call-btn-${o.id}`}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold border border-emerald-100 hover:bg-emerald-100">
                      <Phone size={11}/> Chiama
                    </a>
                  )}
                </div>

                {o.notes_order && (
                  <div className="text-xs text-[#64748B] bg-slate-50 rounded-lg px-3 py-2 mt-2">
                    <b>Note ordine:</b> {o.notes_order}
                  </div>
                )}

                {/* Meta row */}
                <div className="flex items-center gap-3 text-[10px] text-[#64748B] pt-1">
                  {o.zone_label && <span className="flex items-center gap-1"><MapPin size={9}/> {o.zone_label}</span>}
                  {o.eta_min > 0 && <span className="flex items-center gap-1"><Clock size={9}/> ETA {o.eta_min}'</span>}
                  {o.delivery_fee > 0 && <span className="flex items-center gap-1"><Euro size={9}/> Fee {o.delivery_fee.toFixed(2)}€</span>}
                </div>
              </div>

              {/* Actions */}
              {tab === 'active' && (
                <div className="px-4 pb-4 pt-2 flex gap-2">
                  <button onClick={() => markDelivered(o)} data-testid={`deliver-${o.id}`}
                    className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2">
                    <CheckCircle2 size={15}/> Consegnata
                  </button>
                  <button onClick={() => { setIssueModal(o); setIssueReason(''); }} data-testid={`issue-${o.id}`}
                    className="px-4 py-3 rounded-xl text-sm font-bold text-amber-700 border border-amber-200 hover:bg-amber-50 flex items-center justify-center gap-2">
                    <AlertTriangle size={15}/>
                  </button>
                </div>
              )}
              {tab === 'delivered' && o.delivered_at && (
                <div className="px-4 pb-3 text-[10px] text-emerald-700">✅ Consegnato il {new Date(o.delivered_at).toLocaleString('it-IT')}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Issue modal */}
      {issueModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setIssueModal(null)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-md bg-white rounded-t-3xl p-5 pb-8 space-y-4" data-testid="issue-modal">
            <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto"/>
            <h3 className="text-lg font-bold text-[#1A1A1A] flex items-center gap-2"><AlertTriangle size={18} className="text-amber-500"/> Segnala problema</h3>
            <p className="text-xs text-[#64748B]">Ordine #{issueModal.order_number}. Il locale riceverà la tua segnalazione immediatamente.</p>
            <div className="grid grid-cols-2 gap-2">
              {['Cliente non risponde', 'Indirizzo sbagliato', 'Citofono non funziona', 'Altro'].map(r => (
                <button key={r} onClick={() => setIssueReason(r)}
                  className={`py-2.5 rounded-xl text-xs font-semibold border ${issueReason === r ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-50 text-[#64748B] border-slate-200'}`}>
                  {r}
                </button>
              ))}
            </div>
            <textarea value={issueReason} onChange={e => setIssueReason(e.target.value)} rows={3} placeholder="Descrizione (opzionale)"
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-sm" data-testid="issue-reason" />
            <div className="flex gap-2">
              <button onClick={() => setIssueModal(null)} className="flex-1 py-3 rounded-xl text-sm font-bold text-[#64748B] bg-slate-100">Annulla</button>
              <button onClick={reportIssue} data-testid="issue-submit"
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700">Invia</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
